import React, { useState, useEffect, useMemo } from 'react';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import { TextField, Button, Box, Typography, Container, MenuItem, Select, InputLabel, FormControl, Autocomplete, CircularProgress, Modal, List, ListItemButton, ListItemText, Divider } from '@mui/material';
import MaskedInput from './MaskedInput';
import { debounce } from 'lodash';
import { formatUtcForDateTimeLocal } from '../utils/dateTime';
import FormCard from './FormCard';
import { modalSurfaceSx } from '../styles/uiTokens';

function EncomendaForm({ onEncomendaAdded, initialData, onCancelEdit }) {
  const { user } = useAuth();
  const isEditing = Boolean(initialData?.id);
  const [id, setId] = useState(null);
  const [empresa_id, setEmpresaId] = useState('');
  const [data_hora_recebimento, setDataHoraRecebimento] = useState('');
  const [funcionario_recebeu_id, setFuncionarioRecebeuId] = useState('');
  const [remetente_id, setRemetenteId] = useState('');
  const [destinatario_id, setDestinatarioId] = useState('');
  const [funcionario_entregou_id, setFuncionarioEntregouId] = useState('');
  const [pessoa_retirou_id, setPessoaRetirouId] = useState('');
  const [data_hora_retirada, setDataHoraRetirada] = useState('');
  const [codigo_rastreio, setCodigoRastreio] = useState('');
  const [tamanho, setTamanho] = useState('');
  const [observacoes, setObservacoes] = useState('');

  const [imovelFilterId, setImovelFilterId] = useState('');
  const [imovelOptions, setImovelOptions] = useState([]);
  const [selectedImovelFilter, setSelectedImovelFilter] = useState(null);
  const [loadingImoveis, setLoadingImoveis] = useState(false);

  const [openMoradoresModal, setOpenMoradoresModal] = useState(false);
  const [moradoresList, setMoradoresList] = useState([]);
  const [loadingMoradores, setLoadingMoradores] = useState(false);

  const [submitting, setSubmitting] = useState(false);

  const [empresas, setEmpresas] = useState([]);
  const [usuarios, setUsuarios] = useState([]);
  const [remetenteOptions, setRemetenteOptions] = useState([]);
  const [destinatarioOptions, setDestinatarioOptions] = useState([]);
  const [pessoaRetirouOptions, setPessoaRetirouOptions] = useState([]);
  const [selectedRemetente, setSelectedRemetente] = useState(null);
  const [selectedDestinatario, setSelectedDestinatario] = useState(null);
  const [selectedPessoaRetirou, setSelectedPessoaRetirou] = useState(null);
  const [loadingRemetente, setLoadingRemetente] = useState(false);
  const [loadingDestinatario, setLoadingDestinatario] = useState(false);
  const [loadingPessoaRetirou, setLoadingPessoaRetirou] = useState(false);

  const debouncedFetchRemetentes = useMemo(
    () => debounce((searchTerm) => {
      setLoadingRemetente(true);
      api.get(`/pessoas?search=${searchTerm}`)
        .then((response) => setRemetenteOptions(response.data.data || response.data))
        .catch((error) => console.error('Erro ao buscar remetentes:', error))
        .finally(() => setLoadingRemetente(false));
    }, 500),
    []
  );

  const debouncedFetchDestinatarios = useMemo(
    () => debounce((searchTerm) => {
      setLoadingDestinatario(true);
      api.get(`/pessoas?search=${searchTerm}`)
        .then((response) => setDestinatarioOptions(response.data.data || response.data))
        .catch((error) => console.error('Erro ao buscar destinatarios:', error))
        .finally(() => setLoadingDestinatario(false));
    }, 500),
    []
  );

  const debouncedFetchPessoasRetirou = useMemo(
    () => debounce((searchTerm) => {
      setLoadingPessoaRetirou(true);
      api.get(`/pessoas?search=${searchTerm}`)
        .then((response) => setPessoaRetirouOptions(response.data.data || response.data))
        .catch((error) => console.error('Erro ao buscar pessoas (retirada):', error))
        .finally(() => setLoadingPessoaRetirou(false));
    }, 500),
    []
  );

  const debouncedFetchImoveis = useMemo(() =>
    debounce((searchTerm) => {
      setLoadingImoveis(true);
      api.get(`/imoveis?search=${encodeURIComponent(searchTerm)}&page=0&limit=20`)
        .then((response) => setImovelOptions(response.data.data || response.data))
        .catch((error) => console.error('Erro ao buscar imoveis:', error))
        .finally(() => setLoadingImoveis(false));
    }, 500),
    []
  );

  const handleOpenMoradoresModal = () => {
    if (!imovelFilterId) return;
    setLoadingMoradores(true);
    setOpenMoradoresModal(true);
    api.get(`/moradores?imovelId=${imovelFilterId}&limit=100`)
      .then((response) => setMoradoresList(response.data.data || response.data))
      .catch((error) => console.error('Erro ao buscar moradores:', error))
      .finally(() => setLoadingMoradores(false));
  };
  const handleCloseMoradoresModal = () => setOpenMoradoresModal(false);

  const handleSelectMorador = (morador) => {
    const pessoa = { id: morador.pessoa_id, nome: morador.pessoa_nome };
    setSelectedDestinatario(pessoa);
    setDestinatarioId(pessoa.id);
    setDestinatarioOptions([pessoa]);
    setOpenMoradoresModal(false);
  };

  useEffect(() => () => {
    debouncedFetchRemetentes.cancel();
    debouncedFetchDestinatarios.cancel();
    debouncedFetchPessoasRetirou.cancel();
    debouncedFetchImoveis.cancel();
  }, [debouncedFetchRemetentes, debouncedFetchDestinatarios, debouncedFetchPessoasRetirou, debouncedFetchImoveis]);

  useEffect(() => {
    if (user && user.tipo_usuario === 'admin') {
      api.get('/empresas').then(response => setEmpresas(response.data.data || response.data)).catch(error => console.error('Erro ao buscar empresas:', error));
    }
    api.get('/usuarios')
      .then(response => {
        const rows = response.data.data || response.data || [];
        const unicos = rows.filter((u, i, arr) => arr.findIndex((x) => x.id === u.id) === i);
        setUsuarios(unicos);
        if (!initialData && user) {
          setFuncionarioRecebeuId(prev => prev || (unicos.some((u) => u.id === user.id) ? user.id : ''));
        }
      })
      .catch(error => console.error('Erro ao buscar usuarios:', error));

    const fetchPessoaById = async (id, setSelected, setOptions) => {
      if (!id) {
        setSelected(null);
        return;
      }
      try {
        const response = await api.get(`/pessoas?id=${id}`);
        const item = (response.data.data || response.data)[0];
        if (item) {
          setSelected(item);
          setOptions([item]);
        } else {
          setSelected(null);
        }
      } catch (error) {
        console.error('Erro ao buscar pessoa por ID:', error);
        setSelected(null);
      }
    };

    if (initialData) {
      setId(initialData.id);
      setEmpresaId(initialData.empresa_id);
      setDataHoraRecebimento(formatUtcForDateTimeLocal(initialData.data_hora_recebimento, user));
      setFuncionarioRecebeuId(initialData.funcionario_recebeu_id || '');
      setRemetenteId(initialData.remetente_id || '');
      setDestinatarioId(initialData.destinatario_id || '');
      setFuncionarioEntregouId(initialData.funcionario_entregou_id || '');
      setPessoaRetirouId(initialData.pessoa_retirou_id || '');
      setDataHoraRetirada(formatUtcForDateTimeLocal(initialData.data_hora_retirada, user));
      setCodigoRastreio(initialData.codigo_rastreio || '');
      setTamanho(initialData.tamanho || '');
      setObservacoes(initialData.observacoes || '');
      fetchPessoaById(initialData.remetente_id, setSelectedRemetente, setRemetenteOptions);
      fetchPessoaById(initialData.destinatario_id, setSelectedDestinatario, setDestinatarioOptions);
      fetchPessoaById(initialData.pessoa_retirou_id, setSelectedPessoaRetirou, setPessoaRetirouOptions);
    } else {
      // Set defaults for new encomenda
      setId(null);
      setEmpresaId('');
      setDataHoraRecebimento('');
      setFuncionarioRecebeuId('');
      setRemetenteId('');
      setDestinatarioId('');
      setFuncionarioEntregouId('');
      setPessoaRetirouId('');
      setDataHoraRetirada('');
      setCodigoRastreio('');
      setTamanho('');
      setObservacoes('');
      setSelectedRemetente(null);
      setSelectedDestinatario(null);
      setSelectedPessoaRetirou(null);
      setRemetenteOptions([]);
      setDestinatarioOptions([]);
      setPessoaRetirouOptions([]);
    }
  }, [initialData, user]);

  const clearForm = () => {
    setId(null);
    setEmpresaId('');
    setDataHoraRecebimento('');
    setFuncionarioRecebeuId('');
    setRemetenteId('');
    setDestinatarioId('');
    setFuncionarioEntregouId('');
    setPessoaRetirouId('');
    setDataHoraRetirada('');
    setCodigoRastreio('');
    setTamanho('');
    setObservacoes('');
    setSelectedRemetente(null);
    setSelectedDestinatario(null);
    setSelectedPessoaRetirou(null);
    setRemetenteOptions([]);
    setDestinatarioOptions([]);
    setPessoaRetirouOptions([]);
    setImovelFilterId('');
    setImovelOptions([]);
    setSelectedImovelFilter(null);
    setMoradoresList([]);
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    setSubmitting(true);
    const encomendaData = {
      empresa_id: user.tipo_usuario === 'admin' ? empresa_id : user.empresa_id,
      data_hora_recebimento,
      funcionario_recebeu_id,
      remetente_id: remetente_id || null,
      destinatario_id,
      funcionario_entregou_id: funcionario_entregou_id || null,
      pessoa_retirou_id: pessoa_retirou_id || null,
      data_hora_retirada: data_hora_retirada || null,
      codigo_rastreio: codigo_rastreio || null,
      tamanho: tamanho || null,
      observacoes: observacoes || null,
    };

    if (id) {
      api.put(`/encomendas/${id}`, encomendaData)
        .then(response => {
          console.log('Encomenda atualizada:', response.data);
          clearForm();
          onEncomendaAdded();
          onCancelEdit();
        })
        .catch(error => {
          console.error('Erro ao atualizar encomenda:', error);
        })
        .finally(() => setSubmitting(false));
    } else {
      api.post('/encomendas', encomendaData)
        .then(response => {
          console.log('Encomenda adicionada:', response.data);
          clearForm();
          onEncomendaAdded();
        })
        .catch(error => {
          console.error('Erro ao adicionar encomenda:', error);
        })
        .finally(() => setSubmitting(false));
    }
  };

  return (
    <Container maxWidth="md">
      <FormCard component="form" onSubmit={handleSubmit}>
        <Typography variant="h5" gutterBottom>
          {id ? 'Editar Encomenda' : 'Adicionar Nova Encomenda'}
        </Typography>

        {(user && user.tipo_usuario === 'admin') && (
          <FormControl fullWidth margin="normal" required>
            <InputLabel id="empresa-select-label">Empresa</InputLabel>
            <Select
              labelId="empresa-select-label"
              value={empresa_id}
              label="Empresa"
              onChange={(e) => setEmpresaId(e.target.value)}
              disabled={!!initialData}
            >
              {empresas.map((emp) => (
                <MenuItem key={emp.id} value={emp.id}>
                  {emp.nome}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        <TextField
          label="Data e Hora do Recebimento"
          variant="outlined"
          fullWidth
          type="datetime-local"
          value={data_hora_recebimento}
          onChange={(e) => setDataHoraRecebimento(e.target.value)}
          margin="normal"
          InputLabelProps={{ shrink: true }}
          required={isEditing}
          disabled={!isEditing}
          helperText={isEditing ? `Edite no fuso da empresa (${user?.empresa_timezone || 'America/Sao_Paulo'})` : 'Na criação, o horário oficial será definido pelo servidor.'}
        />

        <FormControl fullWidth margin="normal" required>
          <InputLabel id="funcionario-recebeu-select-label">Funcionario que Recebeu</InputLabel>
          <Select
            labelId="funcionario-recebeu-select-label"
            value={funcionario_recebeu_id}
            label="Funcionario que Recebeu"
            onChange={(e) => setFuncionarioRecebeuId(e.target.value)}
            disabled={!!initialData} // Disable if editing
          >
            <MenuItem value=""><em>Nenhum</em></MenuItem>
              {usuarios.map((u) => (
                <MenuItem key={`${u.id}-${u.empresa_id}`} value={u.id}>
                {u.email} ({u.tipo_usuario})
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Autocomplete
          options={remetenteOptions}
          getOptionLabel={(option) => option.nome || ''}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          value={selectedRemetente}
          onChange={(event, newValue) => {
            setSelectedRemetente(newValue);
            setRemetenteId(newValue ? newValue.id : '');
          }}
          onInputChange={(event, newInputValue, reason) => {
            if (reason === 'input') {
              if (newInputValue) debouncedFetchRemetentes(newInputValue);
              else setRemetenteOptions([]);
            }
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Remetente (Pessoa)"
              margin="normal"
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {loadingRemetente ? <CircularProgress color="inherit" size={20} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
        />

        <Autocomplete
          id="imovel-filter-autocomplete"
          options={imovelOptions}
          getOptionLabel={(option) => `${option.descricao} - ${option.numero}` || ''}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          value={selectedImovelFilter}
          onChange={(event, newValue) => {
            setSelectedImovelFilter(newValue);
            setImovelFilterId(newValue ? newValue.id : '');
          }}
          onInputChange={(event, newInputValue, reason) => {
            if (reason === 'input') {
              if (newInputValue) debouncedFetchImoveis(newInputValue);
              else setImovelOptions([]);
            }
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Unidade (Filtro)"
              margin="normal"
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {loadingImoveis ? <CircularProgress color="inherit" size={20} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
        />

        <Autocomplete
          options={destinatarioOptions}
          getOptionLabel={(option) => option.nome || ''}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          value={selectedDestinatario}
          onChange={(event, newValue) => {
            setSelectedDestinatario(newValue);
            setDestinatarioId(newValue ? newValue.id : '');
          }}
          onInputChange={(event, newInputValue, reason) => {
            if (reason === 'input') {
              if (newInputValue) debouncedFetchDestinatarios(newInputValue);
              else setDestinatarioOptions([]);
            }
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Destinatario (Pessoa)"
              margin="normal"
              required
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {loadingDestinatario ? <CircularProgress color="inherit" size={20} /> : null}
                    <Button onClick={handleOpenMoradoresModal} size="small" sx={{ ml: 1 }} disabled={!imovelFilterId}>
                      Moradores
                    </Button>
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
        />

        {isEditing && (
          <>
            <FormControl fullWidth margin="normal">
              <InputLabel id="funcionario-entregou-select-label">Funcionario que Entregou</InputLabel>
              <Select
                labelId="funcionario-entregou-select-label"
                value={funcionario_entregou_id}
                label="Funcionario que Entregou"
                onChange={(e) => setFuncionarioEntregouId(e.target.value)}
              >
                <MenuItem value=""><em>Nenhum</em></MenuItem>
                {usuarios.map((u) => (
                  <MenuItem key={`${u.id}-${u.empresa_id}`} value={u.id}>
                    {u.email} ({u.tipo_usuario})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Autocomplete
              options={pessoaRetirouOptions}
              getOptionLabel={(option) => option.nome || ''}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              value={selectedPessoaRetirou}
              onChange={(event, newValue) => {
                setSelectedPessoaRetirou(newValue);
                setPessoaRetirouId(newValue ? newValue.id : '');
              }}
              onInputChange={(event, newInputValue, reason) => {
                if (reason === 'input') {
                  if (newInputValue) debouncedFetchPessoasRetirou(newInputValue);
                  else setPessoaRetirouOptions([]);
                }
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Pessoa que Retirou"
                  margin="normal"
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {loadingPessoaRetirou ? <CircularProgress color="inherit" size={20} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />

            <TextField
              label="Data e Hora da Retirada"
              variant="outlined"
              fullWidth
              type="datetime-local"
              value={data_hora_retirada}
              onChange={(e) => setDataHoraRetirada(e.target.value)}
              margin="normal"
              InputLabelProps={{ shrink: true }}
            />
          </>
        )}

        <TextField
          label="Codigo de Rastreio"
          variant="outlined"
          fullWidth
          value={codigo_rastreio}
          onChange={(e) => setCodigoRastreio(e.target.value.toUpperCase())}
          margin="normal"
          InputLabelProps={{ shrink: true }}
          InputProps={{
            inputComponent: MaskedInput,
            inputProps: {
              mask: '********************', // 20 alphanumeric characters
              definitions: {
                '*': /[a-zA-Z0-9]/,
              },
              lazy: false,
              placeholderChar: '_',
              unmask: true,
            },
          }}
        />

        <FormControl fullWidth margin="normal">
          <InputLabel id="tamanho-select-label">Tamanho</InputLabel>
          <Select
            labelId="tamanho-select-label"
            value={tamanho}
            label="Tamanho"
            onChange={(e) => setTamanho(e.target.value)}
          >
            <MenuItem value=""><em>Nenhum</em></MenuItem>
            <MenuItem value="pequeno">Pequeno</MenuItem>
            <MenuItem value="medio">Medio</MenuItem>
            <MenuItem value="grande">Grande</MenuItem>
          </Select>
        </FormControl>

        <TextField
          label="Observacoes"
          variant="outlined"
          fullWidth
          multiline
          rows={3}
          value={observacoes}
          onChange={(e) => setObservacoes(e.target.value)}
          margin="normal"
        />

        <Button
          type="submit"
          variant="contained"
          color="primary"
          sx={{ mt: 2, mr: 2 }}
          disabled={submitting}
        >
          {id ? 'Atualizar Encomenda' : 'Adicionar Encomenda'}
        </Button>
        <Button
          type="button"
          variant="outlined"
          color="secondary"
          sx={{ mt: 2 }}
          onClick={onCancelEdit}
        >
          Cancelar
        </Button>
      </FormCard>

      <Modal
        open={openMoradoresModal}
        onClose={handleCloseMoradoresModal}
        aria-labelledby="modal-title-moradores"
      >
        <Box sx={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'center',
          minHeight: '100dvh',
          py: { xs: 1, sm: 2 },
          px: { xs: 1, sm: 2 },
          overflowY: 'auto',
        }}>
          <Box sx={{
            width: { xs: '100%', sm: 520 },
            maxWidth: '100%',
            mt: { xs: 0, sm: 2 },
            maxHeight: 'calc(100dvh - 32px)',
            overflowY: 'auto',
            p: { xs: 2, sm: 4 },
            ...modalSurfaceSx,
          }}>
            <Typography id="modal-title-moradores" variant="h6" component="h2" sx={{ mb: 2 }}>
              Moradores da Unidade
            </Typography>
            {loadingMoradores ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            ) : moradoresList.length === 0 ? (
              <Typography color="text.secondary">Nenhum morador encontrado nesta unidade.</Typography>
            ) : (
              <List disablePadding>
                {moradoresList.map((morador, index) => (
                  <React.Fragment key={morador.id}>
                    {index > 0 && <Divider />}
                    <ListItemButton onClick={() => handleSelectMorador(morador)}>
                      <ListItemText primary={morador.pessoa_nome} secondary={morador.imovel_descricao ? `${morador.imovel_descricao} - ${morador.imovel_numero}` : null} />
                    </ListItemButton>
                  </React.Fragment>
                ))}
              </List>
            )}
          </Box>
        </Box>
      </Modal>
    </Container>
  );
}

export default EncomendaForm;
